import {NgTemplateOutlet} from '@angular/common';
import {Component, HostListener, inject, OnDestroy, OnInit} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {finalize} from 'rxjs';
import {NzAlertModule} from 'ng-zorro-antd/alert';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzCardModule} from 'ng-zorro-antd/card';
import {NzEmptyModule} from 'ng-zorro-antd/empty';
import {NzIconModule} from 'ng-zorro-antd/icon';
import {NzDropDownModule} from 'ng-zorro-antd/dropdown';
import {NzMenuModule} from 'ng-zorro-antd/menu';
import {NzSpinModule} from 'ng-zorro-antd/spin';
import {NzTagModule} from 'ng-zorro-antd/tag';
import {AvailableSystem} from '../core/models/auth.models';
import {AuthFlowService} from '../core/services/auth-flow.service';
import {AuthApiService} from '../core/services/auth-api.service';
import {ModuleCard} from './module-card';

@Component({
  selector: 'app-main',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    TranslatePipe,
    NzAlertModule,
    NzButtonModule,
    NzCardModule,
    NzEmptyModule,
    NzIconModule,
    NzDropDownModule,
    NzMenuModule,
    NzSpinModule,
    NzTagModule,
    RouterLink,
  ],
  templateUrl: './main.component.html',
  styleUrl: './main.component.css'
})
export class MainComponent implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly authFlowService = inject(AuthFlowService);
  private readonly authApiService = inject(AuthApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  primaryCard: ModuleCard | null = null;
  moduleCards: ModuleCard[] = [];
  selectedKey: string | null = null;
  isRedirecting = false;
  private redirectResetTimer: ReturnType<typeof setTimeout> | null = null;

  isLoadingSystems = false;
  errorMessage = '';
  readonly isAdmin = this.authApiService.isAdmin;
  readonly isSuperAdmin = this.authApiService.isSuperAdmin;

  ngOnInit(): void {
    const stateSystems = history.state?.systems;

    if (Array.isArray(stateSystems) && stateSystems.length) {
      this.setSystems(stateSystems);
      return;
    }

    this.loadSystems();
  }

  @HostListener('window:pageshow', ['$event'])
  onPageShow(event: PageTransitionEvent): void {
    if (event.persisted) {
      this.resetRedirect();
    }
  }

  ngOnDestroy(): void {
    this.clearRedirectTimer();
  }

  selectCard(card: ModuleCard): void {
    if (this.isRedirecting) {
      return;
    }

    this.selectedKey = card.key;
    this.errorMessage = '';

    const system = card.system;
    if (!system.loginUrl) {
      this.errorMessage = this.translate.instant('main.noLoginUrl');
      return;
    }

    this.isRedirecting = true;
    this.redirectResetTimer = setTimeout(() => this.resetRedirect(), MainComponent.REDIRECT_RESET_MS);
    window.location.href = this.resolveLoginUrl(system.loginUrl);
  }

  private static readonly REDIRECT_RESET_MS = 8000;

  private resetRedirect(): void {
    this.clearRedirectTimer();
    this.isRedirecting = false;
    this.selectedKey = null;
  }

  private clearRedirectTimer(): void {
    if (this.redirectResetTimer !== null) {
      clearTimeout(this.redirectResetTimer);
      this.redirectResetTimer = null;
    }
  }

  logout(): void {
    this.authApiService.logout();
  }

  private static readonly PRIMARY_AUDIENCE = 'iszh';

  private static readonly SYSTEM_MODULES: Record<string, ReadonlyArray<{ key: string; image: string }>> = {
    iszh: [{key: 'iszh', image: 'assets/iszh.svg'}],
    vetid: [{key: 'pc', image: 'assets/vetid.svg'}],
    vetlab: [
      {key: 'rvl', image: 'assets/vetlab.png'},
      {key: 'rpo', image: 'assets/rpo.svg'},
      {key: 'vse', image: 'assets/vse.svg'},
    ],
  };

  private setSystems(systems: AvailableSystem[]): void {
    const cards = systems.flatMap(system => this.toCards(system));
    this.primaryCard = cards.find(card => card.system.audience?.toLowerCase() === MainComponent.PRIMARY_AUDIENCE) ?? null;
    this.moduleCards = cards.filter(card => card !== this.primaryCard);
  }

  private toCards(system: AvailableSystem): ModuleCard[] {
    const audience = system.audience?.toLowerCase() ?? '';
    const modules = MainComponent.SYSTEM_MODULES[audience];

    if (!modules) {
      return [{
        key: system.id,
        system,
        titleKey: system.name,
        subtitleKey: system.audience || system.clientId || 'main.moduleFallback',
        imageUrl: system.imageUrl || `assets/${system.audience}.svg`,
      }];
    }

    return modules.map(module => ({
      key: module.key,
      system,
      titleKey: `main.modules.${module.key}.title`,
      subtitleKey: `main.modules.${module.key}.subtitle`,
      imageUrl: module.image,
    }));
  }

  private loadSystems(): void {
    this.isLoadingSystems = true;
    this.errorMessage = '';

    this.authFlowService.availableSystems()
      .pipe(finalize(() => this.isLoadingSystems = false))
      .subscribe({
        next: (systems) => {
          this.setSystems(systems);

          if (!systems.length) {
            this.errorMessage = this.translate.instant('main.noModules');
          }
        },
        error: () => {
          this.clearLocalSession();
        }
      });
  }

  private clearLocalSession(): void {
    this.authApiService.clearLocal();
    this.router.navigate(['/login']);
  }

  private static readonly ALLOWED_LAUNCH_ORIGINS = [
    'http://localhost:4200',
    'https://test-3.iszh.kz',
    'https://iszh.gov.kz',
  ];

  private resolveLoginUrl(loginUrl: string): string {
    const origin = this.route.snapshot.queryParamMap.get('origin');
    if (origin && MainComponent.ALLOWED_LAUNCH_ORIGINS.includes(origin)) {
      const u = new URL(loginUrl);
      return origin + u.pathname + u.search;
    }
    return loginUrl;
  }
}
